import { ApiResult, ServerError, ServerUnavailableError, UserError } from "../api";
import { useCallback, useEffect, useState } from "react";
import { useStateWithCallback } from "./useStateWithCallback";
import { useFetchTransform } from "./FetchTransformContext";

type Fetcher<T> = () => ApiResult<T>;
type UseFetchHelpers = {
    isLoading: boolean;
    isRefreshing: boolean;
    refreshData: () => void;
};
type UseFetchResult<T> = [T | undefined, string | undefined, UseFetchHelpers];

export function useFetch<T>(
    fetcher: Fetcher<T>,
    dependencies?: any[]
): UseFetchResult<T> {
    const [data, setData] = useStateWithCallback<T | undefined>(undefined);
    const [error, setError] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState(0);
    const transform = useFetchTransform();

    const refreshData = () => {
        setNeedsRefresh((prev) => prev + 1);
        setIsRefreshing(true);
    };

    const fetchData = useCallback(async () => {
        setIsLoading(true);

        try {
            if (process.env.NODE_ENV !== 'production') {
                console.debug(`fetching data from ${fetcher}`);
            }
            let newData = await fetcher();

            // Apply transform if provided via FetchTransformContext
            if (transform && newData !== undefined) {
                newData = transform(newData);
            }

            if (newData === data) {
                setIsLoading(false);
                setIsRefreshing(false);
            }
            setData(newData, () => {
                setIsLoading(false);
                setIsRefreshing(false);
            });
        } catch (err) {
            setIsLoading(false);
            setIsRefreshing(false);
            if (err instanceof UserError) {
                setError(err.message);
            } else if (err instanceof ServerUnavailableError) {
                setError(err.message);
            } else if (err instanceof ServerError) {
                setError(`Server error: ${err.message}`);
            } else {
                console.log("Error performing fetch", err);
                setError(err + "");
            }
        }
    }, [...(dependencies ?? []), needsRefresh, transform]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setError("");
        fetchData();
    }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

    return [data, error, { isLoading, isRefreshing, refreshData }];
}
